import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  LinearProgress,
  Popover,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { getProviderUsage } from '../../api/llmUsage.api';

function UsageIndicator({ provider, model, darkMode, compact = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Map model names to providers
  const getProviderFromModel = (modelName) => {
    if (!modelName) return null;
    const modelLower = modelName.toLowerCase();
    if (modelLower.includes('groq')) return 'groq';
    if (modelLower.includes('huggingface') || modelLower.includes('hf')) return 'huggingface';
    if (modelLower.includes('together')) return 'together';
    if (modelLower.includes('cohere')) return 'cohere';
    if (modelLower.includes('mistral')) return 'mistral';
    if (modelLower.includes('deepseek')) return 'deepseek';
    if (modelLower.includes('openai') || modelLower.includes('gpt')) return 'openai';
    if (modelLower.includes('anthropic') || modelLower.includes('claude')) return 'anthropic';
    if (modelLower.includes('ollama') || modelLower.includes('llama3')) return 'ollama';
    return provider || null;
  };

  const effectiveProvider = provider || getProviderFromModel(model);

  useEffect(() => {
    if (effectiveProvider && effectiveProvider !== 'ollama') {
      loadUsage();
      // Refresh every 60 seconds
      const interval = setInterval(loadUsage, 60000);
      return () => clearInterval(interval);
    }
  }, [effectiveProvider]);

  const loadUsage = async () => {
    if (!effectiveProvider || effectiveProvider === 'ollama') return;
    
    try {
      setLoading(true);
      const { data } = await getProviderUsage(effectiveProvider, 'daily');
      if (data?.usage) {
        setUsage(data.usage);
      }
    } catch (error) {
      console.error('Error loading usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!effectiveProvider || effectiveProvider === 'ollama') {
    return null; // No limits for Ollama
  }

  if (!usage) {
    return null; // No usage data available
  }

  const percentage = usage.limit ? (usage.used / usage.limit) * 100 : 0;
  const isWarning = percentage >= 75;
  const isError = percentage >= 90;
  const isExceeded = usage.remaining <= 0;

  if (compact) {
    return (
      <>
        <Chip
          icon={isExceeded ? <WarningIcon /> : <CheckCircleIcon />}
          label={`${usage.remaining} left`}
          color={isExceeded ? 'error' : isWarning ? 'warning' : 'success'}
          size="small"
          onClick={handleClick}
          sx={{ cursor: 'pointer' }}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {effectiveProvider.toUpperCase()} Usage
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption">Daily</Typography>
                <Typography variant="caption" fontWeight={600}>
                  {usage.used} / {usage.limit}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentage}
                color={isExceeded ? 'error' : isWarning ? 'warning' : 'success'}
                sx={{ height: 6, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Remaining: {usage.remaining} | Resets: {usage.resetAt ? new Date(usage.resetAt).toLocaleTimeString() : 'N/A'}
              </Typography>
            </Box>
            {isExceeded && (
              <Typography variant="caption" color="error">
                Limit reached. Try a different provider.
              </Typography>
            )}
          </Paper>
        </Popover>
      </>
    );
  }

  return (
    <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          {effectiveProvider.toUpperCase()} Usage
        </Typography>
        <Chip
          label={isExceeded ? 'Limit Reached' : `${usage.remaining} remaining`}
          color={isExceeded ? 'error' : isWarning ? 'warning' : 'success'}
          size="small"
        />
      </Box>
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Daily: {usage.used} / {usage.limit} {usage.limitType || 'requests'}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {Math.round(percentage)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          color={isExceeded ? 'error' : isWarning ? 'warning' : 'success'}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>
      {usage.resetAt && (
        <Typography variant="caption" color="text.secondary">
          Resets: {new Date(usage.resetAt).toLocaleString()}
        </Typography>
      )}
      {isExceeded && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          ⚠️ Daily limit reached. Please use a different provider or wait for reset.
        </Typography>
      )}
    </Box>
  );
}

export default UsageIndicator;
